import { useState, useEffect } from 'react';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { LogMessage } from './types.tsx';

export const COMPASS_PRODUCT_NAME = 'compass';

const logErrorAndFireAnalytics = (errorMessage: LogMessage) => {
	const { packageName, locationId, message, error } = errorMessage;

	log.safeErrorWithoutCustomerData(`${packageName}.${locationId}`, message, error);

	fireErrorAnalytics({
		meta: {
			id: locationId,
			packageName,
		},
		error,
		sendToPrivacyUnsafeSplunk: true,
	});
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const doOnError = (error: any) => {
	logErrorAndFireAnalytics({
		packageName: 'jiraSoftwareCompassCrossFlowTouchPoint',
		locationId: 'services.license-information.has-compass-been-provisioned',
		message: 'Could not fetch license information.',
		error,
	});
};

const getLicenseInformation = (cloudId: string) =>
	performGetRequest(`/gateway/api/xflow/${cloudId}/license-information`);

export const hasCompassBeenProvisioned = async (cloudId: string) => {
	try {
		const data = await getLicenseInformation(cloudId);
		return data?.products?.[COMPASS_PRODUCT_NAME]?.state === 'ACTIVE';
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		doOnError(err);
		throw err;
	}
};

export const useCompassProvisionStatus = () => {
	const cloudId = useCloudId();
	const [status, setStatus] = useState<'loading' | 'error' | 'provisioned' | 'not-provisioned'>(
		'loading',
	);

	useEffect(() => {
		const provisionedCheck = async () => {
			if (await hasCompassBeenProvisioned(cloudId)) {
				setStatus('provisioned');
			} else {
				setStatus('not-provisioned');
			}
		};

		provisionedCheck().catch(() => {
			setStatus('error');
		});
	}, [cloudId]);

	return status;
};
